import { axiosInstance } from 'utils';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { PARAM_AUTOREPAY } from 'utils/constants';
import { useLocation } from 'react-router-dom';
import isLogged from 'utils/isLogged';

type Props = {
  children: ReactNode;
};

export type PaymentPreferences = {
  autoRepay: boolean;
};

type PaymentPreferencesContextType = {
  paymentPreferences: PaymentPreferences | undefined;
  fetchPaymentPreferences: () => Promise<PaymentPreferences | undefined>;
  fetchingPaymentPreferences: boolean;
  updateAutorepay: (autoRepay: boolean) => Promise<PaymentPreferences | void>;
  checkAutorepayParam: () => Promise<void>;
};

const PaymentPreferencesContext = createContext<PaymentPreferencesContextType>(
  {} as PaymentPreferencesContextType,
);

const usePaymentPreferences = (): PaymentPreferencesContextType =>
  useContext(PaymentPreferencesContext);

const PaymentPreferencesProvider = ({ children }: Props): JSX.Element => {
  const [paymentPreferences, setPaymentPreferences] = useState<
    PaymentPreferences | undefined
  >();
  const [fetchingPaymentPreferences, setFetchingPreferences] =
    useState<boolean>(false);

  const location = useLocation();
  const urlParams = new URLSearchParams(
    decodeURIComponent(window.location.search),
  );

  const isClientLogged = isLogged();

  const fetchPaymentPreferences = useCallback(async (): Promise<
    PaymentPreferences | undefined
  > => {
    try {
      setFetchingPreferences(true);
      const fetchedPaymentsPreferences = await axiosInstance.get(
        '/client/payment-preferences',
      );
      setPaymentPreferences(fetchedPaymentsPreferences.data);
      return fetchedPaymentsPreferences.data;
    } catch (e) {
      return undefined;
    } finally {
      setFetchingPreferences(false);
    }
  }, []);

  useEffect(() => {
    const prefetch = async () => {
      if (!paymentPreferences && isClientLogged) {
        try {
          await fetchPaymentPreferences();
        } catch {}
      }
    };
    prefetch();
  }, [location, isClientLogged]);

  const updateAutorepay = useCallback(
    async (autoRepay: boolean): Promise<PaymentPreferences | void> => {
      try {
        setFetchingPreferences(true);
        const fetchedPaymentsPreferences = await axiosInstance.put(
          '/client/payment-preferences/auto-repay',
          { autoRepay },
        );
        setPaymentPreferences(fetchedPaymentsPreferences.data);
        return fetchedPaymentsPreferences.data;
      } catch (e) {
      } finally {
        setFetchingPreferences(false);
      }
    },
    [],
  );

  const checkAutorepayParam = async () => {
    const autoRepayParam = urlParams.get(PARAM_AUTOREPAY);
    if (autoRepayParam) await updateAutorepay(true);
    return;
  };

  const paymentPreferencesContextValue = useMemo(
    () => ({
      checkAutorepayParam,
      paymentPreferences,
      fetchPaymentPreferences,
      fetchingPaymentPreferences,
      updateAutorepay,
    }),
    [
      checkAutorepayParam,
      paymentPreferences,
      fetchingPaymentPreferences,
      fetchPaymentPreferences,
      updateAutorepay,
    ],
  );

  return (
    <PaymentPreferencesContext.Provider value={paymentPreferencesContextValue}>
      {children}
    </PaymentPreferencesContext.Provider>
  );
};

export { usePaymentPreferences as default, PaymentPreferencesProvider };
